"use client";
import { FC, useEffect, useRef, useState } from "react";
import "@videojs/http-streaming";
import videojs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import { Play } from "lucide-react";

interface VideoPlayerProps {
  posterSrc?: string;
  hlsSrc: string;
  options?: Player["options"];
}

export const HLSPlayer: FC<VideoPlayerProps> = ({
  hlsSrc,
  posterSrc,
  options,
  ...props
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add(
        "vjs-big-play-centered",
        "video-js",
        "vjs-default-skin"
      );
      videoRef.current.appendChild(videoElement);

      playerRef.current = videojs(videoElement, {
        autoplay: false,
        controls: true,
        responsive: true,
        fluid: true,
        sources: [{ src: hlsSrc, type: "application/x-mpegURL" }],
        poster: posterSrc,
        html5: {
          hls: {
            overrideNative: true,
            withCredentials: false,
          },
        },
        techOrder: ["html5"],
        ...options,
      });

      // Event listeners
      playerRef.current.on("play", () => setShowOverlay(false));
      playerRef.current.on("pause", () => setShowOverlay(true));
      playerRef.current.on("ended", () => {
        setShowOverlay(true);
        playerRef.current?.currentTime(0);
      });

      playerRef.current.on("error", (event: any) => {
        const error = playerRef.current?.error();
        if (error && error.code !== 0) {
          console.error("Video.js error:", {
            code: error?.code,
            message: error?.message,
            type: error?.code,
          });
        }
      });
    }
  }, [hlsSrc, posterSrc, options]);

  useEffect(() => {
    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player {...props} className="relative w-full h-full">
      <div ref={videoRef} className="w-full h-full z-0" />

      {/* Custom poster + play button overlay */}
      {posterSrc && (
        <div
          className={`absolute inset-0 flex items-center justify-center cursor-pointer bg-black/40 z-20 transition-opacity duration-300 ${
            showOverlay ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => playerRef.current?.play()}
        >
          <img
            src={posterSrc}
            alt="Video Poster"
            className="absolute inset-0 w-full h-full object-cover z-10"
          />
          <div className="relative z-20 bg-black/60 rounded-full p-4 hover:bg-black/80 transition-colors">
            <Play className="h-8 w-8 text-white fill-white" />
          </div>
        </div>
      )}
    </div>
  );
};
